export function isLandscapeViewport() {
  if (typeof window === "undefined") {
    return false;
  }

  const screenOrientation = window.screen?.orientation?.type;
  if (screenOrientation) {
    return screenOrientation.startsWith("landscape");
  }

  if (typeof window.orientation === "number") {
    return Math.abs(window.orientation) === 90;
  }

  const w = window.innerWidth || 0;
  const h = window.innerHeight || 0;
  if (w > 0 && h > 0) {
    return w > h;
  }

  return window.matchMedia("(orientation: landscape)").matches;
}

export function getViewportScreenMode() {
  return isLandscapeViewport() ? "landscap" : "portrait";
}

/**
 * Reliable viewport size after orientation changes (Samsung Chrome safe).
 * Avoids locking to a stale visualViewport half-height right after rotate.
 */
export function getStableViewportBox() {
  if (typeof window === "undefined") {
    return { width: 0, height: 0 };
  }

  const iw = window.innerWidth || 0;
  const ih = window.innerHeight || 0;
  const vv = window.visualViewport;
  const vw = vv?.width || 0;
  const vh = vv?.height || 0;

  let width = iw;
  let height = ih;

  // Only trust visualViewport when it roughly matches the layout viewport.
  if (vw >= iw * 0.9) width = Math.round(vw);
  if (vh >= ih * 0.9) height = Math.round(vh);

  // Landscape sanity: height should not be tiny compared to width.
  if (width > height && height > 0 && height < width * 0.35) {
    height = ih || height;
    width = iw || width;
  }

  return {
    width: Math.max(0, Math.round(width)),
    height: Math.max(0, Math.round(height)),
  };
}

export function subscribeViewportOrientation(onChange) {
  if (typeof window === "undefined") {
    return () => {};
  }

  const timers = [];

  const sync = () => onChange(getViewportScreenMode());

  // Android often updates innerWidth/innerHeight after orientationchange.
  const syncAfterRotate = () => {
    sync();
    [50, 150, 300, 500, 800].forEach((ms) => {
      timers.push(window.setTimeout(sync, ms));
    });
  };

  window.addEventListener("orientationchange", syncAfterRotate);
  window.addEventListener("resize", sync);

  if (window.screen?.orientation?.addEventListener) {
    window.screen.orientation.addEventListener("change", syncAfterRotate);
  }

  window.visualViewport?.addEventListener("resize", sync);

  return () => {
    timers.forEach((id) => window.clearTimeout(id));
    window.removeEventListener("orientationchange", syncAfterRotate);
    window.removeEventListener("resize", sync);
    window.visualViewport?.removeEventListener("resize", sync);

    if (window.screen?.orientation?.removeEventListener) {
      window.screen.orientation.removeEventListener("change", syncAfterRotate);
    }
  };
}
